// Format Firestore Timestamp / Date / ISO string into a relative or short string.

function asDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch (_) {
      return null;
    }
  }
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatRelative(value, locale = "en") {
  const date = asDate(value);
  if (!date) return "";
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.round(diff / 60000);
  const hours = Math.round(diff / 3600000);
  const days = Math.round(diff / 86400000);

  const rtf = typeof Intl !== "undefined" && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
    : null;

  if (Math.abs(minutes) < 1) return rtf ? rtf.format(0, "minute") : "now";
  if (Math.abs(minutes) < 60) return rtf ? rtf.format(-minutes, "minute") : `${minutes}m`;
  if (Math.abs(hours) < 24) return rtf ? rtf.format(-hours, "hour") : `${hours}h`;
  if (Math.abs(days) < 7) return rtf ? rtf.format(-days, "day") : `${days}d`;

  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  } catch (_) {
    return date.toDateString();
  }
}

export function formatLong(value, locale = "en") {
  const date = asDate(value);
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
      timeStyle: "short",
    }).format(date);
  } catch (_) {
    return date.toLocaleString();
  }
}

function capitalize(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/**
 * The section a note falls in, as in the iOS note list: Today, Yesterday,
 * the last 7 days, then one section per month. `last7Label` is passed in
 * because it is the one label `Intl` cannot produce.
 */
export function dateSection(value, locale = "en", last7Label = "Last 7 days") {
  const date = asDate(value);
  if (!date) return { key: "unknown", label: "" };
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const daysAgo = Math.round((startOfToday - startOfDay) / 86400000);
  const rtf =
    typeof Intl !== "undefined" && Intl.RelativeTimeFormat
      ? new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
      : null;
  if (daysAgo <= 0) return { key: "today", label: capitalize(rtf ? rtf.format(0, "day") : "Today") };
  if (daysAgo === 1) return { key: "yesterday", label: capitalize(rtf ? rtf.format(-1, "day") : "Yesterday") };
  if (daysAgo < 7) return { key: "last7", label: last7Label };
  const key = `${date.getFullYear()}-${date.getMonth()}`;
  let label;
  try {
    label = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
  } catch (_) {
    label = key;
  }
  return { key, label: capitalize(label) };
}

/** `14:32` today, the weekday within a week, the short date beyond — as iOS. */
export function formatRowDate(value, locale = "en") {
  const date = asDate(value);
  if (!date) return "";
  const ageDays = (Date.now() - date.getTime()) / 86400000;
  try {
    if (ageDays < 1 && new Date().getDate() === date.getDate()) {
      return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
    }
    if (ageDays < 7) {
      return capitalize(new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date));
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "short" }).format(date);
  } catch (_) {
    return date.toLocaleDateString();
  }
}
